import {
  MicHandler,
  MicStatus,
  AzureSttResponse,
  AzureSttFailure,
  MicOptions,
  MicError,
  MicData,
  RecorderData,
  RecorderHandler,
  RecognizerData
} from "../../../models/microphone";
import {
  MIC_ERR_MEDIA_STREAM,
  MIC_ERR_STT
} from "../../../constants/microphone";

import { noop } from "../../common";
import { isMicDataValid } from "./mic-data";

export abstract class AbstMic implements MicHandler {
  // --------------------------------------------------------------------------
  //    Attributes
  // --------------------------------------------------------------------------

  // --- microphone information
  // Statuses
  protected _status: MicStatus = "idle";
  protected _isProcessing = false;
  // Debugging
  protected _logMsgs: string[] = [];
  protected _debug: boolean;
  // Errors
  protected _error?: MicError = undefined;
  protected _errors: MicError[] = [];
  // Raw recording and recognition info
  protected _micData: MicData[] = [];
  // Tracking
  protected _timeStart = -1;
  protected _timeEnd = -1;

  // -- speech recording
  protected _onRecordingStarted: () => void;
  protected _onRecordingStopped: RecorderHandler;
  protected _onRecordingDelete: RecorderHandler;
  protected _onRecordingError: (error: any) => void;
  protected _onSilenceDetected: () => void;

  // -- speech recognition
  protected _recognizingText: string[] = [];
  protected _onRecognizingStarted: () => void;
  protected _onRecognizingStopped: () => void;
  protected _onRecognizingDelete: (recognized: string[]) => void;
  protected _onRecognizingError: (error: any) => void;

  // -- Azure STT requirements
  protected _azAudioType = "audio/wav";
  protected _azSampleRate = 16000; // 16 kHz

  // --------------------------------------------------------------------------
  //    Initialisation
  // --------------------------------------------------------------------------
  constructor(options: MicOptions = {}) {
    this._debug = options.debug || false;

    this._onRecordingStarted = options.onRecordingStarted || noop;
    this._onRecordingStopped = options.onRecordingStopped || noop;
    this._onRecordingDelete = options.onRecordingDelete || noop;
    this._onRecordingError = options.onRecordingError || noop;

    this._onSilenceDetected = options.onSilenceDetected || noop;

    this._onRecognizingStarted = options.onRecognizingStarted || noop;
    this._onRecognizingStopped = options.onRecognizingStopped || noop;
    this._onRecognizingDelete = options.onRecognizingDelete || noop;
    this._onRecognizingError = options.onRecognizingError || noop;
  }

  // --------------------------------------------------------------------------
  //    Getters
  // --------------------------------------------------------------------------
  get status(): MicStatus {
    return this._status;
  }

  get error(): MicError | undefined {
    return this._error;
  }

  get errors(): MicError[] {
    return this._errors;
  }

  get isProcessing(): boolean {
    return this._isProcessing;
  }

  get micData(): MicData[] {
    return this._micData;
  }

  get logMsgs(): string[] {
    return this._logMsgs;
  }

  get audioInfo(): RecorderData[] {
    return this._micData.map(d => d.recording);
  }

  get audioData(): Blob[] {
    return this.audioInfo.map(a => a.blob);
  }

  get audioUrls(): string[] {
    return this.audioInfo
      .map(a => a.url)
      .filter((url): url is string => url !== undefined);
  }

  get combinedAudioUrls(): string | undefined {
    if (this.audioInfo.length === 0) {
      return undefined;
    }

    const combinedBlob: Blob = new Blob(this.audioData, {
      type: this._azAudioType
    });
    const combinedUrl = URL.createObjectURL(combinedBlob);
    return combinedUrl;
  }

  get recognizedRawResults(): AzureSttResponse[] {
    return (
      this._micData
        .map(d => d.recognition.raw)
        // https://stackoverflow.com/a/54696488/4906586
        .filter((stt): stt is AzureSttResponse => {
          return (stt as AzureSttResponse).MaskedLexical !== undefined;
        })
    );
  }

  get recognizedTexts(): string[] {
    return this._micData.filter(isMicDataValid).map(d => d.recognition.display);
  }

  get recognizingText(): string[] {
    return this._recognizingText;
  }

  // --------------------------------------------------------------------------
  //    Methods: Microphone
  // --------------------------------------------------------------------------
  abstract start(): Promise<void>;

  abstract stop(): Promise<void>;

  abstract cancel(): Promise<void>;

  async delete() {
    // Last valid data finder
    const findLastNonDeleteData = (): MicData | undefined => {
      for (let i = this._micData.length - 1; i >= 0; i--) {
        if (!this._micData[i].deleted) {
          return this._micData[i];
        }
      }

      return undefined;
    };

    // Get latest non-deleted MicData
    const lastData = findLastNonDeleteData();
    if (!lastData) {
      // no error thrown
      this.log(`Delete: no data to delete`);
      return;
    }

    // Flag as deleted and call callbacks accordingly
    lastData.deleted = true;
    this._onRecordingDelete([lastData.recording.blob]);
    this._onRecognizingDelete([lastData.recognition.value]);

    // Reset some status
    this._error = undefined;
    this._isProcessing = false;

    this.log(`Delete: ${lastData.recognition.value.substr(0, 20)}...`);

    if (this._micData.filter(isMicDataValid).length === 0) {
      this._status = "ready";
    }
  }
  /**
   * @deprecated 29-Jun-2020: By using DisplayText as displayed text, text
   * deletion behaviour has changed and deleting last word is no longer
   * required
   */
  async deleteLastWord() {
    throw new Error(`deleteLastWord() is deprecated`);
    // // Find the last index with a non-empty value-text
    // let lastTextIdx = this._micData
    //   .slice()
    //   .reverse()
    //   .findIndex((d) => d.recognition.value);

    // // No text found
    // if (lastTextIdx == -1) {
    //   return;
    // }

    // // reverse the index
    // lastTextIdx = this._micData.length - 1 - lastTextIdx;

    // // Locate the last space in the text
    // let lastText = this._micData[lastTextIdx].recognition.value;
    // let lastSpace = lastText.trim().lastIndexOf(" ");

    // // The last text still has some words left
    // if (lastSpace > 0) {
    //   const withoutLastWord = lastText.substring(0, lastSpace + 1);
    //   this._micData[lastTextIdx].recognition.value = withoutLastWord;
    //   this._micData[lastTextIdx].recognition.display = withoutLastWord;
    // }
    // // It was the last word
    // else {
    //   this._micData[lastTextIdx].recognition.value = "";
    //   this._micData[lastTextIdx].recognition.display = "";
    // }

    // if (this._debug) {
    //   this.log(
    //     `Delete last word results: ${JSON.stringify(
    //       this._micData[lastTextIdx].recognition
    //     )}`
    //   );
    // }
  }

  resetMicData() {
    this._micData = [];
    this._error = undefined;
    this._isProcessing = false;
    this._status = "ready";

    this.log(`Data reset`);
  }

  // --------------------------------------------------------------------------
  //    Methods: Results processing
  // --------------------------------------------------------------------------
  public async addNewData(
    recordingData: RecorderData,
    recognitionData: AzureSttResponse | AzureSttFailure
  ): Promise<MicData> {
    // Build data
    const processRecog = this.processAzureRecognizerData(recognitionData);

    const data: MicData = {
      deleted: false,
      recording: this.processRecorderData(recordingData),
      recognition: processRecog.data
    };

    const recognitionError = processRecog.error;
    if (recognitionError) {
      this.addError(recognitionError, false);
    }

    // Update data
    this._micData = [...this._micData, data];

    return data;
  }

  /**
   * Transform recording data
   * @param recordingData
   */
  protected processRecorderData(recordingData: RecorderData): RecorderData {
    const newBlob =
      recordingData.blob.type === this._azAudioType
        ? recordingData.blob
        : new Blob([recordingData.blob], { type: this._azAudioType });
    const newURL = recordingData.url || URL.createObjectURL(newBlob);

    console.log(newURL);

    return {
      ...recordingData,
      blob: newBlob,
      url: newURL,
      error: recordingData.error
    };
  }

  /**
   * Transform recognition data
   * @param recognitionData
   */
  protected processAzureRecognizerData(
    recognitionData: AzureSttResponse | AzureSttFailure
  ): { data: RecognizerData; error?: MicError } {
    // Recognizer data
    const respAsSuccess = recognitionData as AzureSttResponse;
    const respAsFailure = recognitionData as AzureSttFailure;

    let displayText = "";
    let valueText = "";
    let error: MicError | undefined = undefined;

    // Lexical can be ""
    if (respAsSuccess.Lexical !== undefined) {
      valueText = respAsSuccess.MaskedLexical || respAsSuccess.Lexical;
      displayText =
        respAsSuccess.MaskedDisplayText ||
        // Fallback for non profanity filtered text
        respAsSuccess.DisplayText ||
        // It should never happen but ensure that the display is never empty
        valueText;

      // Timeout check.
      if (respAsSuccess.request_timed_out) {
        error = MIC_ERR_STT.SOFT_TIMEOUT;
      }
    }

    // --- Failure /o\
    else if (respAsFailure.cancelled_reason) {
      const reason = respAsFailure.cancelled_reason;

      // Audio is not consistent?
      if (reason.includes("EndOfStream")) {
        error = { ...MIC_ERR_STT.END_OF_STREAM, details: respAsFailure };
      }
      // Audio is OK but not the content
      else if (reason.includes("failed to transcribe")) {
        error = {
          ...MIC_ERR_STT.FAILED_TRANSCRIPTION,
          details: respAsFailure
        };
      }
      // Unknown case
      else {
        error = {
          key: "mic-err_unknown",
          details: respAsFailure
        };
      }
    }

    return {
      data: {
        display: displayText,
        value: valueText,
        raw: recognitionData,
        error
      },
      error
    };
  }

  // --------------------------------------------------------------------------
  //    Methods: Errors handling
  // --------------------------------------------------------------------------
  protected async addError(err: MicError, isInitError = false) {
    if (isInitError) {
      this._status = "init-err";
      this.log(`ERROR: _status switched to "${this._status}"`);
    }

    this._error = err;
    this._errors = [...this._errors, err];

    this.log(`ERROR: ${JSON.stringify(err)}`);
  }

  protected addErrorFromMediaStream(
    mediaStreamErr: MediaStreamError,
    otherDetails?: any
  ) {
    const micErr = Object.values(MIC_ERR_MEDIA_STREAM).find(
      micErr => micErr.value === mediaStreamErr.name
    );

    if (micErr) {
      this.addError(
        { ...micErr, details: { ...otherDetails, ...mediaStreamErr } },
        true
      );
    }
  }

  protected addUnknownError(details: any) {
    this.addError({
      key: "mic-err_unknown",
      details
    });
  }

  // --------------------------------------------------------------------------
  //    Methods: Misc
  // --------------------------------------------------------------------------
  protected log(str: string): void {
    this._logMsgs = [...this._logMsgs, str];
    if (this._debug) {
      console.log(`%c${str}`, "color: #00FF00;");
    }
  }
}
